import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChangeEvent } from 'react';
import {
  Search, Receipt, Eye, Plus, ListChecks, Layers,
} from 'lucide-react';
import { useTranslation } from '../../i18n';

import { Card } from '../../components/Shared/Card';
import { Loading } from '../../components/Shared/Loading';
import { Button } from '../../components/Shared/Button';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Pagination } from '../../components/Shared/Pagination';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
  type TableSortDirection,
} from '../../components/Shared/Table';
import { Badge } from '../../components/Shared/Badge';
import {
  useInvoiceStore,
  type InvoiceListQuery,
  type InvoiceSortBy,
  type InvoiceSortDir,
} from '../../store/useInvoiceStore';
import type { FacilityFlowCoreModulesInvoicesDtosInvoiceListDto as InvoiceListDto } from '@/api/generated/models';
import { useCustomerStore } from '../../store/useCustomerStore';
import { formatDate } from '@/utils/dateUtils';
import { getObjectColors } from '@/utils/objectColors';
import { formatMoney } from '@/utils/money';
import {
  BILLING_INTERVALS,
  billingIntervalLabelKey,
  formatInvoicePeriod,
  isBillingInterval,
  isStandingInvoice,
} from '@/types/billing';
import {
  CURRENT_YEAR, useInvoiceListState,
  type InvoiceIntervalFilter, type InvoiceStatusFilter,
} from './hooks/useInvoiceListState';
import { getInvoiceStatusBadge } from './statusBadge';

const MONTHS = [
  { value: 1, key: '1' }, { value: 2, key: '2' }, { value: 3, key: '3' },
  { value: 4, key: '4' }, { value: 5, key: '5' }, { value: 6, key: '6' },
  { value: 7, key: '7' }, { value: 8, key: '8' }, { value: 9, key: '9' },
  { value: 10, key: '10' }, { value: 11, key: '11' }, { value: 12, key: '12' },
];

/** Direction a column starts with on its first click — amounts and dates largest/newest first. */
const DEFAULT_SORT_DIR: Record<InvoiceSortBy, InvoiceSortDir> = {
  Object: 'Asc',
  Customer: 'Asc',
  InvoiceNumber: 'Asc',
  Period: 'Desc',
  Status: 'Asc',
  Gross: 'Desc',
  DueDate: 'Desc',
};

export default function InvoicesPage() {
  const { t } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();

  const {
    invoices, loading, error, fetchInvoices,
    totalCount, page, pageSize, lastQuery,
  } = useInvoiceStore();

  const { customers, fetchCustomers } = useCustomerStore();

  const {
    searchTerm, filterCustomerId, filterStatus, filterInterval, sortBy, sortDir, listPage,
    filterYear, filterMonth, groupByObject, setListState,
  } = useInvoiceListState();
  // Only typing is debounced — the list query follows this, not every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Single source for the list query, so filters and sort survive paging and retry.
  const buildQuery = useCallback((nextPage: number): InvoiceListQuery => ({
    page: nextPage,
    pageSize: pageSize || DEFAULT_PAGE_SIZE,
    search: debouncedSearch.trim() || undefined,
    customerId: filterCustomerId !== 'all' ? filterCustomerId : undefined,
    status: filterStatus !== 'all' ? filterStatus : undefined,
    year: filterYear !== 'all' ? filterYear : undefined,
    month: filterMonth !== 'all' ? filterMonth : undefined,
    interval: filterInterval !== 'all' ? filterInterval : undefined,
    sortBy,
    sortDir,
    groupByObject,
  }), [
    pageSize, debouncedSearch, filterCustomerId, filterStatus,
    filterYear, filterMonth, filterInterval, sortBy, sortDir, groupByObject,
  ]);

  useEffect(() => {
    fetchCustomers({ pageSize: 200 });
  }, [fetchCustomers]);

  // Typing keeps the page (see onSearchChange); the settled term resets it together with the
  // search, so one fetch goes out instead of a page-1 fetch with the old term first.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchTerm === debouncedSearch) return;
      setDebouncedSearch(searchTerm);
      setListState({ page: 1 });
    }, 350);
    return () => window.clearTimeout(handle);
  }, [searchTerm, debouncedSearch, setListState]);

  // Sort clicks, filters and the settled search term refetch right away; changing a filter or
  // sort resets the URL page to 1 (useUrlListState), paging fetches the requested page.
  useEffect(() => {
    fetchInvoices(buildQuery(listPage)).then(() => setInitialLoadComplete(true));
  }, [buildQuery, listPage, fetchInvoices]);

  const pageCount = useMemo(() => {
    const safePageSize = pageSize || DEFAULT_PAGE_SIZE;
    return Math.max(1, Math.ceil((totalCount || 0) / safePageSize));
  }, [totalCount, pageSize]);

  const goToPage = (nextPage: number) => {
    setListState({ page: Math.max(1, Math.min(pageCount, nextPage)) });
  };

  // Same column toggles the direction; a new column starts with its natural direction.
  // Either change rebuilds the query, which refetches page 1 right away (effect above).
  const handleSort = (column: InvoiceSortBy) => {
    if (column === sortBy) {
      setListState({ dir: sortDir === 'Asc' ? 'Desc' : 'Asc' });
    } else {
      setListState({ sort: column, dir: DEFAULT_SORT_DIR[column] });
    }
  };

  const navigateToInvoice = (id?: string | null) => {
    if (id) navigate(`/invoices/${id}`);
  };

  if (loading && !initialLoadComplete) {
    return <Loading />;
  }

  const hasActiveFilter =
    !!searchTerm
    || filterCustomerId !== 'all'
    || filterStatus !== 'all'
    || filterMonth !== 'all'
    || filterInterval !== 'all';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('invoices:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('invoices:subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              variant="secondary"
              onClick={() => navigate('/invoices/new/manual')}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('invoices:actions.createManual')}
            </Button>
            <Button
              onClick={() => navigate('/invoices/new')}
              leftIcon={<ListChecks className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto"
            >
              {t('invoices:actions.create')}
            </Button>
          </div>
        </div>
      </div>

      <FiltersCard
        searchTerm={searchTerm}
        onSearchChange={(e) => setListState({ q: e.target.value, page: listPage })}
        filterCustomerId={filterCustomerId}
        onCustomerChange={(e) => setListState({ customer: e.target.value })}
        filterStatus={filterStatus}
        onStatusChange={(e) =>
          setListState({ status: e.target.value as InvoiceStatusFilter })
        }
        filterYear={filterYear}
        onYearChange={(e) => setListState({ year: e.target.value })}
        filterMonth={filterMonth}
        onMonthChange={(e) => setListState({ month: e.target.value })}
        filterInterval={filterInterval}
        onIntervalChange={(e) =>
          setListState({ interval: isBillingInterval(e.target.value) ? e.target.value : 'all' })
        }
        groupByObject={groupByObject}
        onToggleGroupByObject={() => setListState({ group: groupByObject ? 'none' : 'object' })}
        customers={customers}
        t={t}
      />

      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('invoices:messages.loadError')}
            description={error}
            onRetry={() => fetchInvoices(lastQuery ?? buildQuery(page))}
          />
        </Card>
      ) : invoices.length === 0 ? (
        <EmptyState
          icon={<Receipt className="w-12 h-12" strokeWidth={1.75} />}
          title={hasActiveFilter
            ? t('invoices:empty.titleFiltered')
            : t('invoices:empty.title')}
          description={hasActiveFilter
            ? t('invoices:empty.descriptionFiltered')
            : t('invoices:empty.description')}
        />
      ) : (
        <div className="space-y-3">
          <Card noPadding>
            <InvoicesTable
              invoices={invoices}
              sortBy={sortBy}
              sortDir={sortDir}
              onSort={handleSort}
              grouped={groupByObject}
              navigateToInvoice={navigateToInvoice}
              t={t}
            />
          </Card>

          <Pagination
            page={page}
            pageCount={pageCount}
            totalCount={totalCount}
            onPageChange={goToPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}

interface FiltersCardProps {
  searchTerm: string;
  onSearchChange: (e: ChangeEvent<HTMLInputElement>) => void;
  filterCustomerId: string;
  onCustomerChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  filterStatus: InvoiceStatusFilter;
  onStatusChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  filterYear: number | 'all';
  onYearChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  filterMonth: number | 'all';
  onMonthChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  filterInterval: InvoiceIntervalFilter;
  onIntervalChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  groupByObject: boolean;
  onToggleGroupByObject: () => void;
  customers: { id?: string; name?: string | null }[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function FiltersCard({
  searchTerm, onSearchChange,
  filterCustomerId, onCustomerChange,
  filterStatus, onStatusChange,
  filterYear, onYearChange,
  filterMonth, onMonthChange,
  filterInterval, onIntervalChange,
  groupByObject, onToggleGroupByObject,
  customers, t,
}: FiltersCardProps) {
  const years = [CURRENT_YEAR + 1, CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];
  const selectClass = 'px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50';
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5">
      {/* One wrapping row from sm up — the selects box dissolves (sm:contents) so search,
          selects and the group toggle wrap item by item instead of as one block. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:items-center">
        <div className="flex-1 sm:min-w-[240px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={2} />
            <input
              type="text"
              placeholder={t('invoices:filter.searchPlaceholder')}
              value={searchTerm}
              onChange={onSearchChange}
              className="w-full pl-9 pr-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30 focus:border-[#00A871]/50"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:contents">
          <select
            value={filterCustomerId}
            onChange={onCustomerChange}
            aria-label={t('invoices:filter.customer')}
            className={selectClass}
          >
            <option value="all">{t('invoices:filter.allCustomers')}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={onStatusChange}
            aria-label={t('invoices:filter.status')}
            className={selectClass}
          >
            <option value="all">{t('invoices:filter.allStatus')}</option>
            <option value="Draft">{t('invoices:status.draft')}</option>
            <option value="Issued">{t('invoices:status.issued')}</option>
            <option value="Sent">{t('invoices:status.sent')}</option>
            <option value="Paid">{t('invoices:status.paid')}</option>
            <option value="Cancelled">{t('invoices:status.cancelled')}</option>
          </select>

          <select
            value={String(filterYear)}
            onChange={onYearChange}
            aria-label={t('invoices:filter.year')}
            className={selectClass}
          >
            <option value="all">{t('invoices:filter.allYears')}</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <select
            value={String(filterMonth)}
            onChange={onMonthChange}
            aria-label={t('invoices:filter.month')}
            className={selectClass}
          >
            <option value="all">{t('invoices:filter.allMonths')}</option>
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>
                {t(`common:scheduleRule.months.${m.key}`)}
              </option>
            ))}
          </select>

          <select
            value={filterInterval}
            onChange={onIntervalChange}
            aria-label={t('invoices:filter.interval')}
            className={selectClass}
          >
            <option value="all">{t('invoices:filter.allIntervals')}</option>
            {BILLING_INTERVALS.map((interval) => (
              <option key={interval} value={interval}>
                {t(billingIntervalLabelKey(interval))}
              </option>
            ))}
          </select>

          <button
            type="button"
            aria-pressed={groupByObject}
            onClick={onToggleGroupByObject}
            className={`col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border text-[13px] whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00A871]/30 ${
              groupByObject
                ? 'border-[#00A871] bg-emerald-50 text-emerald-800 font-medium'
                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Layers className="w-3.5 h-3.5 shrink-0" strokeWidth={2} aria-hidden="true" />
            {t('invoices:table.groupByObject')}
          </button>
        </div>
      </div>
    </div>
  );
}

const COLUMN_COUNT = 8;

interface InvoicesTableProps {
  invoices: InvoiceListDto[];
  sortBy: InvoiceSortBy;
  sortDir: InvoiceSortDir;
  onSort: (column: InvoiceSortBy) => void;
  /** Object subheading rows — the server keeps each object's invoices together while grouped. */
  grouped: boolean;
  navigateToInvoice: (id?: string | null) => void;
  t: (key: string) => string;
}

function InvoicesTable({
  invoices, sortBy, sortDir, onSort, grouped,
  navigateToInvoice, t,
}: InvoicesTableProps) {
  const sortProps = (column: InvoiceSortBy) => ({
    sortable: true,
    sortDirection: (column === sortBy
      ? (sortDir === 'Asc' ? 'asc' : 'desc')
      : null) as TableSortDirection | null,
    onSort: () => onSort(column),
  });

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader {...sortProps('InvoiceNumber')}>{t('invoices:table.invoiceNumber')}</TableHeader>
          <TableHeader {...sortProps('Customer')}>{t('invoices:table.customer')}</TableHeader>
          <TableHeader className="hidden sm:table-cell" {...sortProps('Object')}>
            {t('invoices:table.object')}
          </TableHeader>
          <TableHeader className="hidden md:table-cell" {...sortProps('Period')}>
            {t('invoices:table.period')}
          </TableHeader>
          <TableHeader {...sortProps('Status')}>{t('invoices:table.status')}</TableHeader>
          <TableHeader {...sortProps('Gross')}>{t('invoices:table.gross')}</TableHeader>
          <TableHeader className="hidden sm:table-cell" {...sortProps('DueDate')}>
            {t('invoices:table.dueDate')}
          </TableHeader>
          <TableHeader>{t('invoices:table.actions')}</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {invoices.map((inv, index) => {
          // why: grouped by id, not name — two customers may each have a „Hauptgebäude"
          // (the server keeps each object's invoices together the same way).
          const groupKey = inv.objectId ?? null;
          const startsGroup = grouped
            && (index === 0 || (invoices[index - 1].objectId ?? null) !== groupKey);
          const endsGroup = grouped
            && (index === invoices.length - 1 || (invoices[index + 1].objectId ?? null) !== groupKey);
          // Object-Color-Rail (design guidelines §8); invoices without an object stay neutral.
          const rail = groupKey ? getObjectColors(groupKey).color : undefined;
          return (
            <Fragment key={inv.id}>
              {startsGroup && (
                <TableRow className="bg-slate-50">
                  <TableCell colSpan={COLUMN_COUNT} className="relative py-2.5">
                    <GroupRail color={rail} className="top-2.5 bottom-0 rounded-t-full" />
                    <div className="flex items-baseline gap-2 min-w-0 pl-3">
                      {groupKey ? (
                        <>
                          <span className="text-[13px] font-semibold text-slate-900 truncate">
                            {inv.objectName || '—'}
                          </span>
                          {/* Customer tells same-named objects apart. */}
                          <span className="text-[12px] text-slate-500 truncate">
                            {inv.customerName}
                          </span>
                        </>
                      ) : (
                        <span className="text-[13px] font-semibold text-slate-600">
                          {t('invoices:table.withoutObject')}
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              <InvoiceRow
                invoice={inv}
                navigateToInvoice={navigateToInvoice}
                rail={grouped ? { color: rail, last: endsGroup } : undefined}
                t={t}
              />
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface GroupRailProps {
  /** Object color; `undefined` = invoices without an object (neutral). */
  color?: string;
  /** Vertical extent and rounding — the rail runs from the group header to its last row. */
  className: string;
}

/**
 * 4px object stripe at the table's left edge. Header and rows share its x-position, so the
 * header reads as the full-width parent and the indented rows hang below it.
 */
function GroupRail({ color, className }: GroupRailProps) {
  return (
    <span
      aria-hidden="true"
      className={`absolute left-3 sm:left-4 w-1 ${color ? '' : 'bg-slate-300'} ${className}`}
      style={color ? { background: color } : undefined}
    />
  );
}

interface InvoiceRowProps {
  invoice: InvoiceListDto;
  navigateToInvoice: (id?: string | null) => void;
  /** Set while grouped: the row continues its group's rail and indents its content. */
  rail?: { color?: string; last: boolean };
  t: (key: string) => string;
}

function InvoiceRow({
  invoice, navigateToInvoice, rail, t,
}: InvoiceRowProps) {
  const handleClick = () => navigateToInvoice(invoice.id);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigateToInvoice(invoice.id);
    }
  };

  const cellProps = {
    role: 'button' as const,
    tabIndex: 0,
    onClick: handleClick,
    onKeyDown: handleKeyDown,
    className: 'outline-none',
  };

  const period = formatInvoicePeriod(invoice);
  const isStanding = isStandingInvoice(invoice.billingInterval);

  return (
    <TableRow>
      <TableCell className={`cursor-pointer ${rail ? 'relative pl-12 sm:pl-14' : ''}`}>
        {rail && (
          <GroupRail
            color={rail.color}
            className={rail.last ? 'top-0 bottom-3 rounded-b-full' : 'inset-y-0'}
          />
        )}
        <div {...cellProps}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono-num text-[13px] font-medium text-slate-900">
              {invoice.invoiceNumber || (
                <span className="italic text-slate-500">
                  {t('invoices:table.draft')}
                </span>
              )}
            </span>
            {isStanding && (
              <Badge variant="info">{t('invoices:kind.standing')}</Badge>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 sm:hidden">
            {invoice.customerName || '—'}
          </div>
        </div>
      </TableCell>

      <TableCell className="cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 outline-none">
          {invoice.customerName || '—'}
        </div>
      </TableCell>

      <TableCell className="hidden sm:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 outline-none">
          {invoice.objectId ? (
            invoice.objectName || '—'
          ) : (
            <span className="text-slate-500 italic">
              {t('invoices:fields.manual')}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="hidden md:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 tabular-nums outline-none">
          {period}
        </div>
      </TableCell>

      <TableCell className="cursor-pointer">
        <div {...cellProps}>
          {getInvoiceStatusBadge(invoice.status, t)}
        </div>
      </TableCell>

      <TableCell className="cursor-pointer text-right">
        <div {...cellProps} className="text-[13px] font-medium text-slate-900 tabular-nums outline-none">
          {formatMoney(invoice.totalGrossAmount, { currency: invoice.currency })}
        </div>
      </TableCell>

      <TableCell className="hidden sm:table-cell cursor-pointer">
        <div {...cellProps} className="text-[13px] text-slate-700 tabular-nums outline-none">
          {formatDate(invoice.dueDate ?? undefined)}
        </div>
      </TableCell>

      <TableCell>
        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigateToInvoice(invoice.id);
            }}
            title={t('common:actions.view')}
            aria-label={t('common:actions.view')}
            className="p-1.5"
          >
            <Eye className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
